import Axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type CustomParamsSerializer,
} from 'axios'
import type {
  WonHttpError,
  RequestMethods,
  WonHttpResponse,
  WonHttpRequestConfig,
} from './types.d'
import { stringify } from 'qs'
import { useUserStore } from '@/store/modules/useUserStore'
import { emitter } from '@/common/mitt'
const { VITE_API_BASE_URL } = import.meta.env

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig: AxiosRequestConfig = {
  baseURL: VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json, text/plain, */*',
    // 'Content-Type': 'application/x-www-form-urlencoded',
    'Content-Type': 'application/json',
    // X-Requested-With 会触发跨域预检（Preflight）请求（OPTIONS 方法），服务端必须在 OPTIONS 预检响应中通过 Access-Control-Allow-Headers 明确声明允许 X-Requested-With，否则将导致 CORS 问题。
    // "X-Requested-With": "XMLHttpRequest"
  },
  // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
  paramsSerializer: {
    serialize: stringify as unknown as CustomParamsSerializer,
  },
}

class WonHttp {
  constructor() {
    this.httpInterceptorsRequest()
    this.httpInterceptorsResponse()
  }
  private static initConfig: WonHttpRequestConfig = {}
  private static axiosInstance: AxiosInstance = Axios.create(defaultConfig)
  private httpInterceptorsRequest(): void {
    WonHttp.axiosInstance.interceptors.request.use(
      async (config: WonHttpRequestConfig): Promise<any> => {
        let token: string = useUserStore().getToken
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        console.log('http request', config)
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }
  private httpInterceptorsResponse(): void {
    const instance = WonHttp.axiosInstance
    instance.interceptors.response.use(
      (response: WonHttpResponse) => {
        const $config = response.config
        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof $config.beforeResponseCallback === 'function') {
          $config.beforeResponseCallback(response)
          return this.parseResponse(response)
        }
        if (WonHttp.initConfig.beforeResponseCallback) {
          WonHttp.initConfig.beforeResponseCallback(response)
          return this.parseResponse(response)
        }
        return this.parseResponse(response)
      },
      (error: WonHttpError) => {
        const $error = error
        $error.isCancelRequest = Axios.isCancel($error)
        // 所有的响应异常 区分来源为取消请求/非取消请求
        return Promise.reject($error)
      }
    )
  }
  private parseResponse(response: WonHttpResponse) {
    console.log('http response', response)
    let data = response.data
    if (response.status == 200 && data.status == 0) {
      return data.data
    } else {
      return Promise.reject(data)
    }
  }

  /** 通用请求工具函数 */
  public request<T>(
    method: RequestMethods,
    url: string,
    param?: AxiosRequestConfig,
    axiosConfig?: WonHttpRequestConfig
  ): Promise<T> {
    // param = this.appendToken(method, param)
    url = `${url}${url.includes('?') ? '&' : '?'}app=2`
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig,
    } as WonHttpRequestConfig
    // 单独处理自定义请求/响应回调
    return new Promise((resolve, reject) => {
      WonHttp.axiosInstance
        .request(config)
        .then((response: undefined) => {
          resolve(response)
        })
        .catch((error) => {
          if (error.status == 401) {
            useUserStore().logout()
            emitter.emit('showLoginDialog')
          }
          reject(error)
        })
    })
  }

  /** 单独抽离的`post`工具函数 */
  public post<T>(
    url: string,
    params?: AxiosRequestConfig,
    config?: WonHttpRequestConfig
  ): Promise<T> {
    return this.request<T>('post', url, params, config)
  }

  /** 单独抽离的`get`工具函数 */
  public get<T>(
    url: string,
    params?: AxiosRequestConfig,
    config?: WonHttpRequestConfig
  ): Promise<T> {
    return this.request<T>('get', url, params, config)
  }
}

export const http = new WonHttp()
